'use client';

import { IEducationResponse } from '@sapientpro/sapientpro-data-models';
import classnames from 'classnames';
import { useAtomValue, useSetAtom } from 'jotai';
import React from 'react';
import { MouseParallax } from 'react-just-parallax';
import AnimatedTitle from '../../components/AnimatedTitle';
import Button, { ButtonVariants } from '../../components/Button';
import CareerTabs from '../../components/CareerTabs';
import Ellipse from '../../components/Ellipse';
import isEducationModalOpen from '../../store/educationForm';
import { theme, Theme } from '../../store/theme';
import styles from './Education.module.scss';
import EducationBasics from './EducationBasics';
import EducationBenefits from './EducationBenefits';
import EducationFormModal from './EducationFormModal';
import EducationReviews from './EducationReviews/EducationReviews';
import Mentors from './Mentors';
import { GAEmit, GAEvents } from '../../gtagHelper';
import { EllipseVariants } from '../../../constants';

type AcademyProps = {
  data: IEducationResponse
};
const Education = ({ data }: AcademyProps) => {
  const appTheme = useAtomValue<Theme>(theme);
  const setIsEducationModalOpen = useSetAtom(isEducationModalOpen);

  const handleSubscribeButtonClick = () => {
    setIsEducationModalOpen(true);
    GAEmit(GAEvents.subscribeClick);
  };

  return (
    <main className={styles.academy}>
      <section className={classnames(styles.hero)}>
        <MouseParallax
          shouldPause={false}
          isAbsolutelyPositioned
          strength={0.2}
        >
          <Ellipse
            variant={EllipseVariants.NOISE}
            className={classnames(styles.ellipse, styles.ellipse_noise)}
            size={179}
          />
        </MouseParallax>
        <MouseParallax
          shouldPause={false}
          isAbsolutelyPositioned
          strength={0.08}
        >
          <Ellipse
            variant={EllipseVariants.GRADIENT}
            className={classnames(styles.ellipse, styles.ellipse_gradient)}
            size={79}
          />
        </MouseParallax>
        <div className='container'>
          <CareerTabs className={styles.tabs} />
          <h1 className={classnames(styles.title, 'title3')}><AnimatedTitle title='SapientPro: Learning Opportunities' /></h1>
          <p className={styles.subtitle}>
            Become a professional software engineer with SapientPro! Join our list and immerse in the world of
            coding, creative work, and exciting encounters.
          </p>
          <Button
            variant={ButtonVariants.OUTLINED}
            className={styles.subscribeButton}
            icon={(
              <svg>
                <use
                  xlinkHref='/media/icon-nest.svg#iconNest'
                  href='/media/icon-nest.svg#iconNest'
                />
              </svg>
            )}
            iconPosition='right'
            iconSize={{ width: 24, height: 24 }}
            onClick={handleSubscribeButtonClick}
          >
            Subscribe
          </Button>
          <img
            className={styles.illustration}
            src={`/media/academy_${appTheme}.webp`}
            alt='academy'
          />
        </div>
      </section>
      <EducationBenefits data={data.benefits} />
      <EducationBasics data={data.basics} />
      <Mentors data={data.leadershipTeam} />
      <EducationReviews data={data.reviews} />
      <EducationFormModal />
    </main>
  );
};

export default Education;
